/**
 * The designed state for a refusal a client meets on their own money.
 *
 * Three lines, always in the same order and the same places: what happened, what
 * it means, what to do next. A client who hits two different refusals in a week
 * should recognise the shape of the second one immediately.
 *
 * Not styled as an error. A refusal here is usually a protection working as
 * intended, and red shouting at someone who did nothing wrong reads as a fault
 * they must have caused.
 */
import type { MoneyRefusal } from '../../lib/moneyRefusal';
import { legendForRefusal } from '../../lib/settlementTiming';
import BusinessDayLegend from './BusinessDayLegend';
import { colors } from '../../styles/theme';

export default function RefusalNotice({ refusal }: Readonly<{
  refusal: MoneyRefusal;
}>): JSX.Element {
  return (
    <section
      role="status"
      aria-live="polite"
      className="rounded-xl p-4 mb-4"
      style={{ background: colors.warningBg, border: '1px solid #FCD34D' }}
    >
      <p className="text-sm font-semibold mb-1" style={{ color: colors.ink }}>
        {refusal.title}
      </p>
      <p className="text-sm" style={{ color: colors.inkSoft }}>{refusal.detail}</p>
      {refusal.next && (
        <p className="text-sm mt-2 font-medium" style={{ color: colors.ink }}>{refusal.next}</p>
      )}
      {/* A refusal that ends on a DATE carries the caveat about that date, and
          one that does not carries nothing. The platform counts Monday to
          Friday and no longer keeps a bank-holiday calendar, so a date here can
          be a day before the bank opens — which is worth saying where the date
          is, and nowhere else. */}
      {refusal.clearsOn !== null && (
        <div className="mt-3">
          <BusinessDayLegend legend={legendForRefusal(refusal.clearsOn)} />
        </div>
      )}
    </section>
  );
}
